/**
 * AI との連携。繋ぎかたの案内と、担当者ごとの MCP 接続キーの発行・停止。
 *
 * 接続キーの発行と一覧は、ページの一番下の折りたたみ（既定は閉じる）に置く。
 * Claude はサインインで繋ぐのが本線で、接続キーはそれ以外のクライアント向けである。
 *
 * この面が守っていること:
 * - **秘密の値は発行直後の 1 回だけ出す。** 閉じたら消え、取り直す口は無い。
 *   一覧に出るのは接頭辞（`vda_xxxxxxxx…`）だけ
 * - **数えていないものを 0 と描かない。** 最終利用も今月の呼び出し数も
 *   API が必ず null を返すので、「数えていません」と理由を出す
 * - **動かせないつまみを描かない。** 共有範囲と許可操作はすべて鍵の印つきで、
 *   なぜ動かせないかを添える
 * - 時刻は生の ISO を出さず、日本時間の表記に揃える
 */

import { Collapse } from "antd";
import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  AiConnectionsDocument,
  Button,
  Card,
  ErrorState,
  formatJstDateTime,
  IssueAiConnectionDocument,
  LoadingState,
  PageHeader,
  SetAiConnectionStatusDocument,
  Tag,
} from "visumo-data-agent-web-shared";

import {
  AI_CONNECTION_LOCKED_NOTE,
  AI_CONNECTION_PLAINTEXT_SHOWN_ONCE_NOTE,
  AI_CONNECTION_REVOKE_NOTE,
  AI_CONNECTION_USAGE_NOT_MEASURED_NOTE,
  MAX_ACTIVE_AI_CONNECTIONS,
  MAX_AI_DEPARTMENT_LENGTH,
  MAX_AI_MEMBER_ID_LENGTH,
  MAX_AI_MEMBER_NAME_LENGTH,
  NOT_MEASURED,
} from "../api-capabilities";
import { HelpIcon } from "../help/HelpIcon";
import { useViewer } from "../ViewerContext";

interface PageUserError {
  readonly code: string;
  readonly message: string;
}

type ClientKind = "CHATGPT" | "CLAUDE" | "GEMINI" | "OTHER";

const CLIENT_LABEL: Record<string, string> = {
  CHATGPT: "ChatGPT",
  CLAUDE: "Claude",
  GEMINI: "Gemini",
  OTHER: "その他の AI",
};

const CLIENT_OPTIONS: readonly ClientKind[] = [
  "CHATGPT",
  "CLAUDE",
  "GEMINI",
  "OTHER",
];

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "使えます",
  SUSPENDED: "停止中",
};

/**
 * 代行の断り書き。**JSX の中で折り返さない**（日本語の途中に空白が入り、
 * 画面にもテストにもその空白が出る）。
 */
const DELEGATION_ISSUE_NOTE =
  "権限の範囲を広げる操作は代行から外してあります。停止は代行でもできます。発行と再開はできません。";

const DELEGATION_RESUME_NOTE =
  "止めるのは代行でもできます。再開は、事業者の管理者から行ってください。";

/**
 * 接続キーの区画。Claude はサインインで繋ぐのが本線なので、発行と一覧は
 * ページの一番下へ畳んで置く（既定は閉じる）。見出しの直下の 1 行は、
 * 閉じたままでも読めるように折りたたみの見出し側に置く。
 */
const KEYS_SECTION_TITLE = "他の AI サービス向け（接続キー）";

const KEYS_SECTION_NOTE =
  "Claude はサインインで繋ぎます。接続キーは Authorization ヘッダを送れるクライアント向けです。";

const OPERATION_LABEL: Record<string, string> = {
  READ: "読む",
  WRITE: "書き換える",
  RUN_STOP: "配信を始める・止める",
  ADMINISTER: "設定を変える",
};

export function AiConnectionsPage() {
  /**
   * 代行中にできるのは**停止だけ**である(`DELEGATION_FORBIDDEN` の
   * `AI_CONNECTION` / `ISSUE` と `RESUME`)。止めるのは事故を止める操作なので
   * 残すが、発行と再開は権限の範囲を広げる側なので代行から外してある。
   * 判断の正本はサーバーの `decide()` で、ここは**押す前に分かる**ようにする
   * ためのものである(押してから断られるだけにしない)。
   */
  const viewer = useViewer();
  const delegated = viewer.role === "CS_DELEGATE";
  const [result, refetch] = useQuery({
    query: AiConnectionsDocument,
    // ほかの端末からも増える。開き直したら読み直す
    requestPolicy: "cache-and-network",
  });
  const [issueState, issue] = useMutation(IssueAiConnectionDocument);
  const [statusState, setStatus] = useMutation(SetAiConnectionStatusDocument);
  const [userErrors, setUserErrors] = useState<PageUserError[]>([]);
  const [memberName, setMemberName] = useState("");
  const [memberId, setMemberId] = useState("");
  const [department, setDepartment] = useState("");
  const [client, setClient] = useState<ClientKind>("CHATGPT");
  /** 発行直後に 1 回だけ出す値。閉じると消える（再表示の口は無い）。 */
  const [issued, setIssued] = useState<{
    memberName: string;
    plaintext: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const connections = result.data.aiConnections;
  const guides = result.data.aiConnectGuides;
  const busy = issueState.fetching || statusState.fetching;
  const activeCount = connections.filter(
    (connection) => connection.status === "ACTIVE",
  ).length;
  // 共有範囲と許可操作はどの接続でも同じなので、1 件目の形をそのまま出す
  const shared = connections[0]?.shared ?? [];
  const operations = connections[0]?.operations ?? [];

  const failWith = (message: string): void => {
    setUserErrors([{ code: "INTERNAL_ERROR", message }]);
  };

  const submitIssue = (): void => {
    const name = memberName.trim();
    const id = memberId.trim();
    if (name.length === 0 || id.length === 0) return;
    const scope = department.trim();
    void issue({
      input: {
        memberName: name,
        memberId: id,
        ...(scope.length === 0 ? {} : { department: scope }),
        client,
      },
    }).then((response) => {
      if (response.error !== undefined) {
        failWith(`接続キーを発行できませんでした: ${String(response.error)}`);
        return;
      }
      const payload = response.data?.issueAiConnection;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({ code, message })),
        );
        return;
      }
      setUserErrors([]);
      if (payload.plaintext !== null && payload.plaintext !== undefined) {
        setIssued({ memberName: name, plaintext: payload.plaintext });
        setCopied(false);
      }
      setMemberName("");
      setMemberId("");
      setDepartment("");
      refetch({ requestPolicy: "network-only" });
    });
  };

  const toggle = (connectionId: string, next: "ACTIVE" | "SUSPENDED"): void => {
    void setStatus({ input: { connectionId, status: next } }).then(
      (response) => {
        if (response.error !== undefined) {
          failWith(`切り替えられませんでした: ${String(response.error)}`);
          return;
        }
        const payload = response.data?.setAiConnectionStatus;
        if (payload === undefined) return;
        if (payload.userErrors.length > 0) {
          setUserErrors(
            payload.userErrors.map(({ code, message }) => ({ code, message })),
          );
          return;
        }
        setUserErrors([]);
        refetch({ requestPolicy: "network-only" });
      },
    );
  };

  const copyPlaintext = (): void => {
    if (issued === null) return;
    void navigator.clipboard
      ?.writeText(issued.plaintext)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="AI との連携"
        description="担当者ごとに接続キーを発行して、この会社のデータを ChatGPT や Claude から読めるようにします。渡すのは読み取りだけです。"
        titleExtra={<HelpIcon slug="integrations/ai-connections" />}
      />

      <div className="vd-stack">
        {userErrors.length === 0 ? null : (
          <div className="vd-notice vd-notice--error" role="alert">
            {userErrors.map((error) => (
              <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))}
          </div>
        )}

        <Card
          title="繋ぎかた"
          extra={
            <HelpIcon slug="integrations/ai-connections" anchor="繋ぎかた" />
          }
          note="接続先は、いまお使いの MCP の入り口と同じです。新しい入り口は増えていません。"
        >
          {guides.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              接続先がまだ設定されていません。設定が済むまで、この案内は出せません（推測した
              URL は載せません）。
            </p>
          ) : (
            <div className="vd-stack">
              {guides.map((guide) => (
                <div key={guide.client} data-testid="ai-connect-guide">
                  <div className="vd-card-title">
                    {CLIENT_LABEL[guide.client] ?? guide.client}
                  </div>
                  <ol>
                    {guide.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  <code
                    className="vd-input"
                    style={{ display: "block", wordBreak: "break-all" }}
                  >
                    {guide.endpointUrl}
                  </code>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="利用量" note={AI_CONNECTION_USAGE_NOT_MEASURED_NOTE}>
          <p style={{ margin: 0 }}>{NOT_MEASURED}</p>
        </Card>

        <Card title="どこまで見えるか" note={AI_CONNECTION_LOCKED_NOTE}>
          <div className="vd-stack">
            <div>
              <div className="vd-card-title">共有する範囲</div>
              <ul>
                {shared.map((scope) => (
                  <li key={scope.key}>
                    {/* 鍵の印つき。動かせるつまみを描かない */}
                    <Tag tone={scope.enabled ? "ok" : "neutral"}>
                      {scope.enabled ? "渡します" : "渡しません"}
                    </Tag>{" "}
                    <span>{scope.label}</span>
                    {scope.locked ? "（変えられません）" : ""}
                    {scope.note === null || scope.note === undefined ? null : (
                      <span className="vd-stat-note"> {scope.note}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="vd-card-title">できる操作</div>
              <ul>
                {operations.map((operation) => (
                  <li key={operation.permission}>
                    <Tag tone={operation.enabled ? "ok" : "neutral"}>
                      {operation.enabled ? "できます" : "できません"}
                    </Tag>{" "}
                    <span>
                      {OPERATION_LABEL[operation.permission] ?? operation.label}
                    </span>
                    {operation.locked ? "（変えられません）" : ""}
                  </li>
                ))}
              </ul>
            </div>
            {connections.length === 0 ? (
              <p className="vd-stat-note" style={{ margin: 0 }}>
                接続キーを 1
                本発行すると、実際に渡る範囲がここに並びます。渡すのは読み取りだけで、投稿者の個人情報はどの設定でも渡しません。
              </p>
            ) : null}
          </div>
        </Card>

        <Collapse
          className="vd-ai-keys"
          data-testid="ai-connection-keys"
          items={[
            {
              key: "keys",
              label: (
                <div>
                  <div className="vd-card-title">{KEYS_SECTION_TITLE}</div>
                  <p className="vd-stat-note" style={{ margin: 0 }}>
                    {KEYS_SECTION_NOTE}
                  </p>
                </div>
              ),
              children: (
                <div className="vd-stack">
                  {issued === null ? null : (
                    <Card
                      title="発行した接続キー"
                      note={AI_CONNECTION_PLAINTEXT_SHOWN_ONCE_NOTE}
                    >
                      <div className="vd-stack">
                        <p style={{ margin: 0 }}>
                          {issued.memberName} さんの接続キーです。
                        </p>
                        <code
                          className="vd-input"
                          data-testid="ai-connection-plaintext"
                          style={{ display: "block", wordBreak: "break-all" }}
                        >
                          {issued.plaintext}
                        </code>
                        <div className="vd-row">
                          <Button variant="primary" onClick={copyPlaintext}>
                            コピーする
                          </Button>
                          <Button
                            onClick={() => {
                              setIssued(null);
                              setCopied(false);
                            }}
                          >
                            閉じる（もう表示できません）
                          </Button>
                          {copied ? <Tag tone="ok">コピーしました</Tag> : null}
                        </div>
                      </div>
                    </Card>
                  )}

                  <Card
                    title="この会社のデータを、誰がどの AI から見ているか"
                    extra={<HelpIcon slug="integrations/ai-connections" />}
                    note={`有効な接続は ${String(activeCount)} 件です（上限 ${String(MAX_ACTIVE_AI_CONNECTIONS)} 件）。`}
                  >
                    {connections.length === 0 ? (
                      <p className="vd-card-note" style={{ marginTop: 0 }}>
                        まだ誰も繋いでいません。下の「接続キーを発行する」から、担当者ぶんの接続キーを作ってください。
                      </p>
                    ) : (
                      <div className="vd-table-scroll">
                        <table className="vd-table">
                          <thead>
                            <tr>
                              <th>氏名</th>
                              <th>社員 ID</th>
                              <th>部署</th>
                              <th>AI サービス</th>
                              <th>使用モデル</th>
                              <th>接続キー</th>
                              <th>接続開始</th>
                              <th>状態</th>
                              <th>操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            {connections.map((connection) => (
                              <tr
                                key={connection.id}
                                data-testid="ai-connection-row"
                              >
                                <td>{connection.memberName}</td>
                                <td>{connection.memberId}</td>
                                <td>{connection.department ?? "—"}</td>
                                <td>
                                  {CLIENT_LABEL[connection.client] ??
                                    connection.client}
                                </td>
                                <td>
                                  {/* 受け取る口が無い。名前を推し量らない */}
                                  <span className="vd-stat-note">
                                    {connection.model ?? connection.modelSource}
                                  </span>
                                </td>
                                <td>
                                  {/* 秘密の断片はここだけ。先頭 8 文字から復元はできない */}
                                  <code>{connection.clientIdMasked}…</code>
                                </td>
                                <td>
                                  {formatJstDateTime(connection.connectedAt)}
                                </td>
                                <td>
                                  <Tag
                                    tone={
                                      connection.status === "ACTIVE"
                                        ? "ok"
                                        : "neutral"
                                    }
                                  >
                                    {STATUS_LABEL[connection.status] ??
                                      connection.status}
                                  </Tag>
                                </td>
                                <td>
                                  <Button
                                    // 代行では再開できない。止めるほうは残す
                                    disabled={
                                      busy ||
                                      (delegated &&
                                        connection.status !== "ACTIVE")
                                    }
                                    aria-label={`${connection.memberName} さんの接続を${connection.status === "ACTIVE" ? "止める" : "再開する"}`}
                                    onClick={() =>
                                      toggle(
                                        connection.id,
                                        connection.status === "ACTIVE"
                                          ? "SUSPENDED"
                                          : "ACTIVE",
                                      )
                                    }
                                  >
                                    {connection.status === "ACTIVE"
                                      ? "止める"
                                      : "再開する"}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {delegated ? (
                      // 表の中で「再開する」が押せない理由を、押す前に読めるようにする
                      <p
                        className="vd-notice"
                        role="status"
                        data-testid="delegation-resume-forbidden"
                      >
                        <strong>代行中は、止めた接続を再開できません。</strong>
                        {DELEGATION_RESUME_NOTE}
                      </p>
                    ) : null}
                    <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                      {AI_CONNECTION_REVOKE_NOTE}
                    </p>
                  </Card>

                  <Card
                    title="接続キーを発行する"
                    extra={
                      <HelpIcon
                        slug="integrations/ai-connections"
                        anchor="接続キーを発行する"
                      />
                    }
                    note="担当者 1 人につき 1 本です。発行した直後の 1 回しか表示できません。"
                  >
                    <div className="vd-stack">
                      {delegated ? (
                        <p
                          className="vd-notice"
                          role="status"
                          data-testid="delegation-forbidden"
                        >
                          <strong>代行中は接続キーを発行できません。</strong>
                          {DELEGATION_ISSUE_NOTE}
                        </p>
                      ) : null}
                      <label className="vd-field">
                        <span className="vd-field-hint">氏名</span>
                        <input
                          className="vd-input"
                          value={memberName}
                          maxLength={MAX_AI_MEMBER_NAME_LENGTH}
                          placeholder="例: 山田 花子"
                          onChange={(event) =>
                            setMemberName(event.target.value)
                          }
                        />
                      </label>
                      <label className="vd-field">
                        <span className="vd-field-hint">社員 ID</span>
                        <input
                          className="vd-input"
                          value={memberId}
                          maxLength={MAX_AI_MEMBER_ID_LENGTH}
                          placeholder="例: EMP-001"
                          onChange={(event) => setMemberId(event.target.value)}
                        />
                      </label>
                      <label className="vd-field">
                        <span className="vd-field-hint">部署（任意）</span>
                        <input
                          className="vd-input"
                          value={department}
                          maxLength={MAX_AI_DEPARTMENT_LENGTH}
                          placeholder="例: EC 運営"
                          onChange={(event) =>
                            setDepartment(event.target.value)
                          }
                        />
                      </label>
                      <label className="vd-field">
                        <span className="vd-field-hint">AI サービス</span>
                        <select
                          className="vd-select"
                          value={client}
                          onChange={(event) =>
                            setClient(event.target.value as ClientKind)
                          }
                        >
                          {CLIENT_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {CLIENT_LABEL[option]}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div>
                        <Button
                          variant="primary"
                          disabled={
                            busy ||
                            delegated ||
                            memberName.trim().length === 0 ||
                            memberId.trim().length === 0
                          }
                          onClick={submitIssue}
                        >
                          接続キーを発行する
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
